import { useMutation } from '@tanstack/react-query';
import type { z } from 'zod';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { generateAiPetitionDraft } from '#src/app/pages/startAPetitionV2/shared/api';
import { useImageJob } from '#src/app/pages/startAPetitionV2/shared/hooks';

import type { schema } from '../../schema';
import { useInferScopeLocation } from '../useInferScopeLocation';

type FormData = z.infer<typeof schema>;

export function useGenerateAiDraft() {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const inferScopeLocation = useInferScopeLocation();

	const { mutateAsync: createJobAsync } = useImageJob();

	const { mutate, error, ...mutationState } = useMutation({
		mutationFn: async (formData: FormData) => {
			// TODO: remove non-null assertion with schema validation _just incase_
			const input = { mainPrompt: data.draft?.input?.issuePrompt ?? '', personalStory: formData.personalStory };

			// Scope/location inference (CHANGE-97331 + CHANGE-97720) is settled alongside the draft/image so
			// its result lands on the draft deterministically at this submit, rather than racing a later step
			// via a self-submit. It can reject; `allSettled` means that never fails this submit. Durable
			// once-per-draft: a prior successful result is reused instead of re-fired (survives the remount
			// when the starter goes back to revise their story); a prior failure leaves this null and retries.
			const existingInference = data.draft?.metadata?.inferredScopeLocation ?? null;

			const [aiDraftResponse, imageResponse, inferenceResponse] = await Promise.allSettled([
				generateAiPetitionDraft(input, utilityContext, track),
				createJobAsync(input),
				existingInference ? Promise.resolve(existingInference) : inferScopeLocation(input),
			]);

			if (aiDraftResponse.status === 'rejected') throw aiDraftResponse.reason;

			const image =
				imageResponse.status === 'fulfilled' ? { image: { responseId: imageResponse.value.jobId } } : undefined;

			const inferredScopeLocation =
				inferenceResponse.status === 'fulfilled' ? inferenceResponse.value : existingInference;

			return { ...aiDraftResponse.value, ...image, inferredScopeLocation };
		},
		onMutate: (formData) => {
			void track('sap_step_submit', {
				step: 'personal_story',
				step_data: { personal_story: formData.personalStory },
				step_data_raw: JSON.stringify({ personal_story: formData.personalStory }),
			});
		},
		onSuccess: (response) => {
			const recommendedDraft = { ask: response.ask, description: response.description, image: null };
			const metadata = {
				imageJobId: response.image?.responseId ?? null,
				inferredScopeLocation: response.inferredScopeLocation,
			};
			actions.send({
				type: 'submit',
				draft: { recommendedDraft, petitionDraft: recommendedDraft, editingDraft: null, metadata },
			});
		},
	});

	return { mutate, error, mutationState };
}
